import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { contentId } from 'Config/configs/ids';
import executeAfterDOMEvent from 'Lib/executeAfterDOMEvent';
import requestIdleCallbackManager from 'Lib/requestIdleCallbackManager';
import { loadWorkboxWindow, registerServiceWorker } from 'Lib/serviceWorkerHelper';
import { UiEngineProvider } from 'Lib/UiEngine';
import { getIsFocusVisibleSupported } from 'Lib/utils/focusVisible';

import HubbleTrackerProvider from 'Store/sparks/trackers/hubble/providers/HubbleTrackerProvider';

import App from 'Containers/App';
import KeyboardShortcutsProvider from 'Containers/SettingsPage/A11y/KeyboardShortcutsProvider';
import LanguageProvider from 'Containers/SettingsPage/LanguageSwitch/LanguageProvider';

import { initializeInstance as initializeStoreInstance, initializeStoreServices } from './store';

// https://caniuse.com/css-focus-visible
const loadFocusVisiblePolyfill = () => {
  if (!getIsFocusVisibleSupported()) {
    import('focus-visible' /* webpackChunkName: "focus-visible-polyfill" */);
  }
};

const dest = document.getElementById(contentId) as HTMLElement;

// Initialize the store asynchronously
const store = initializeStoreInstance();
initializeStoreServices(store).then(() => {
  hydrateRoot(
    dest,
    <StrictMode>
      <Provider store={store}>
        <LanguageProvider>
          <UiEngineProvider>
            <BrowserRouter>
              <HubbleTrackerProvider>
                <KeyboardShortcutsProvider>
                  <App />
                </KeyboardShortcutsProvider>
              </HubbleTrackerProvider>
            </BrowserRouter>
          </UiEngineProvider>
        </LanguageProvider>
      </Provider>
    </StrictMode>
  );

  // Things that we can delay
  executeAfterDOMEvent('load', () => {
    // not blocking init load
    requestIdleCallbackManager.addTask(() => {
      // workbox
      loadWorkboxWindow(() => {
        registerServiceWorker(store.dispatch);
      });

      // For keyboard focus style
      loadFocusVisiblePolyfill();
    });
  });
});
